<template>
  <div class="animated fadeIn">
    <!-- Loader -->
    <b-row v-if="loading">
      <b-col md="6">
        <atom-spinner
          :animation-duration="1000"
          :size="60"
          color="#ff1d5e"
          class="float-right my-4"
        />
      </b-col>
    </b-row>

    <!-- Main Content -->
    <div v-else>
      <component-header
        title="Advertisements"
        @search-query="val => (filter = val)"
        :isLoading="loading"
        @reload-data="getAdvertisementList"
        :hasback="true"
        @back-press="backBtn"
        @create-press="$router.push({ path: '/advertisement/create' })"
      />

      <b-card class="card-wrap" border-variant="info">
        <div>
          <b-table
            class="tableRadius"
            responsive
            :fields="adsColumns"
            :items="paginatedAds"
            :dark="dark"
            :hover="hover"
            thead-class="header"
            :striped="striped"
            :bordered="bordered"
            :small="small"
            show-empty
            :fixed="fixed"
            ref="ads"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
          >
            <!-- Active -->
            <template #cell(active)="data">
              <div class="table-row">
                <c-switch
                  class="mx-1"
                  color="primary"
                  v-model="data.item.active"
                  label
                  variant="pill"
                  data-on="yes"
                  data-off="yes"
                />
              </div>
            </template>

            <!-- Visibility -->
            <template #cell(visibility)="data">
              <div class="table-row">
                {{
                  (data.item &&
                    data.item.target &&
                    data.item.target.visibility &&
                    data.item.target.visibility[0]) ||
                  (data.item &&
                    data.item.target &&
                    data.item.target.visibility &&
                    data.item.target.visibility.type) ||
                  "-"
                }}
              </div>
            </template>

            <!-- Icon -->
            <template #cell(icon)="data">
              <div class="table-row text-center">
                <img
                  v-if="data.item.icon"
                  :src="data.item.icon"
                  alt="Ad Icon"
                  style="height: 80px; width: 80px; object-fit: cover;"
                />
                <span v-else>-</span>
              </div>
            </template>

            <!-- Actions -->
            <template #cell(action)="data">
              <div class="inline_btnTable">
                <b-button
                  size="sm"
                  class="button-circle mr-2 view"
                  @click="viewAd(data.item)"
                >
                  <i class="fa fa-eye"></i>
                </b-button>
                <b-button
                  size="sm"
                  class="button-circle mr-2 pencil"
                  @click="editAd(data.item)"
                >
                  <i class="fas fa-pencil-alt"></i>
                </b-button>
                <b-button
                  size="sm"
                  class="button-circle delete"
                  @click="deleteAd(data.item)"
                >
                  <i class="fas fa-trash-alt"></i>
                </b-button>
              </div>
            </template>
          </b-table>
        </div>

        <!-- Pagination -->
        <div style="float: right" v-if="filterCount > perPage">
          <b-pagination
            v-model="currentPage"
            :per-page="perPage"
            :total-rows="filterCount"
            aria-controls="ads"
            prev-text="Prev"
            next-text="Next"
            hide-goto-end-buttons
          ></b-pagination>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import ebButton from "@/components/FormElements/Button";
import ComponentHeader from "@/components/componentNewHeader";
import { Switch as cSwitch } from "@coreui/vue";
import { mapGetters } from "vuex";
import { AtomSpinner } from "epic-spinners";

export default {
  name: "Advertisement",
  components: {
    ebButton,
    cSwitch,
    ComponentHeader,
    AtomSpinner,
  },
  data() {
    return {
      partner: {},
      loading: false,
      currentPage: 1,
      filter: "",
      perPage: 5,
      hover: true,
      striped: false,
      bordered: true,
      small: false,
      fixed: false,
      dark: false,
      sortBy: "createdAt",
      sortDesc: true,
      adsColumns: [
        { key: "icon", label: "Banner" },
        { key: "type", label: "Type" },
        { key: "placement", label: "Placement" },
        { key: "visibility", label: "Visibility" },
        { key: "active", label: "Active" },
        { key: "action", label: "Action" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      AdList: "advertisement/GetAdList",
      userId: "auth/getUserID",
      partnerAllData: "partner/GetPartnerData",
    }),

    // All ads
    ads() {
      const list = Array.isArray(this.AdList) ? this.AdList : [this.AdList];
      return list
        .filter(Boolean)
        .map((obj) => ({
          ...obj,
          locationRadius:
            obj && obj.target ? obj.target.locationRadius : null,
          icon:
            obj && obj.icon && obj.icon.fileUrl
              ? obj.icon.fileUrl
              : "",
        }));
    },

    // Filtered ads
    filteredAds() {
      if (!this.filter) return this.ads;
      const term = this.filter.toLowerCase();
      return this.ads.filter(
        (ad) =>
          (ad.type && ad.type.toLowerCase().includes(term)) ||
          (ad.placement && ad.placement.toLowerCase().includes(term))
      );
    },

    // Paginated ads
    paginatedAds() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredAds.slice(start, start + this.perPage);
    },

    // For pagination count
    filterCount() {
      return this.filteredAds.length;
    },
  },

  methods: {
    viewAd(data) {
      this.$router.push({
        name: "view-advertisement",
        params: { id: data._id },
      });
    },
    editAd(data) {
      this.$router.push({
        name: "edit-advertisement",
        params: { id: data._id },
      });
    },
    deleteAd(data) {
      this.$swal({
        title: "Delete this Ad?",
        text: "Are you sure? You won't be able to revert this!",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, Delete it!",
        reverseButtons: true,
      }).then((result) => {
        if (result.value) {
          this.$store
            .dispatch("advertisement/deleteAd", data._id)
            .then(() => {
              this.$toasted.success("Advertisement Deleted Successfully!");
              this.getAdvertisementList();
            })
            .catch(() => {
              this.$toasted.error("Ad not deleted");
            });
        }
      });
    },
    getAdvertisementList() {
      this.loading = true;
      let payload =
        this.$currentUserData.category === "SELLER"
          ? { provider: this.$currentUserData._id, all: true }
          : { all: true };
      if (this.$route.params.id) {
        payload.provider = this.$route.params.id;
      }
      this.$store
        .dispatch("advertisement/fetchAdListforTenant", payload)
        .finally(() => {
          this.loading = false;
        });
    },
    backBtn() {
      if (this.$route.params.id) {
        this.$router.push("./basic-info");
      } else {
        this.$router.push("/home");
      }
    },
  },

  mounted() {
    this.getAdvertisementList();
  },
};
</script>

<style>
.text-center {
  text-align: center;
}
.form-div label {
  margin-top: 8px;
}
</style>
